import styled from 'styled-components';

import { ClienteVendaCheckoutSecao } from '../../../../../../../types/cliente-venda';
import { CollapsibleSection } from '../../produtos/sections/collapsible-section';
import { formatDateTime, formatPreco, simNao } from '../format';
import { Row } from './styles';

interface Props {
  data: ClienteVendaCheckoutSecao | null;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 18px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ParcelasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px;
  margin-top: 6px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;

  div {
    display: flex;
    justify-content: space-between;
    padding: 4px 8px;
    background: ${({ theme }) => theme.colors.bgMuted};
    border-radius: ${({ theme }) => theme.radius.sm};
  }
`;

const SubBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;

  h4 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

export const CheckoutSection = ({ data }: Props) => {
  if (!data) {
    return (
      <CollapsibleSection title="Checkout" testId="venda-section-checkout">
        <Row>
          <span>—</span>
          <span>Checkout não disponível</span>
        </Row>
      </CollapsibleSection>
    );
  }
  const parcelas = Object.entries(data.valoresParcelas);
  return (
    <CollapsibleSection title="Checkout" testId="venda-section-checkout">
      <Grid>
        <Row>
          <span>ID</span>
          <span>{data.id ?? '—'}</span>
        </Row>
        <Row>
          <span>Data</span>
          <span>{formatDateTime(data.createdAt)}</span>
        </Row>
        <Row>
          <span>Valor</span>
          <span>{formatPreco(data.valor, data.moedaSigla)}</span>
        </Row>
        <Row>
          <span>Subtotal</span>
          <span>{formatPreco(data.subtotal, data.moedaSigla)}</span>
        </Row>
        <Row>
          <span>Total</span>
          <span>{formatPreco(data.total, data.moedaSigla)}</span>
        </Row>
        <Row>
          <span>Moeda</span>
          <span>{data.moedaSigla ?? '—'}</span>
        </Row>
        <Row>
          <span>País</span>
          <span>{data.pais ?? '—'}</span>
        </Row>
        <Row>
          <span>Linguagem</span>
          <span>{data.linguagem ?? '—'}</span>
        </Row>
        <Row>
          <span>IP</span>
          <span>{data.ip ?? '—'}</span>
        </Row>
        <Row>
          <span>Métodos de pagamento</span>
          <span>{data.metodosPagamento ?? '—'}</span>
        </Row>
        <Row>
          <span>Máximo de parcelas</span>
          <span>{data.maximoParcelas ?? '—'}</span>
        </Row>
        <Row>
          <span>Recorrência</span>
          <span>{simNao(data.recorrencia)}</span>
        </Row>
        <Row>
          <span>Exibir endereço</span>
          <span>{simNao(data.exibirEndereco)}</span>
        </Row>
        <Row>
          <span>Exibir cupom</span>
          <span>{simNao(data.exibirCupomDesconto)}</span>
        </Row>
      </Grid>

      {data.cupom && (
        <SubBlock>
          <h4>Cupom aplicado</h4>
          <Row>
            <span>Código</span>
            <span>{data.cupom.codigo ?? '—'}</span>
          </Row>
          <Row>
            <span>Desconto</span>
            <span>
              {data.cupom.desconto != null
                ? `${data.cupom.desconto}`
                : '—'}
            </span>
          </Row>
        </SubBlock>
      )}

      {data.afiliado && (
        <SubBlock>
          <h4>Afiliado</h4>
          <Row>
            <span>{data.afiliado.nome} {data.afiliado.sobrenome ?? ''}</span>
            <span>{data.afiliado.email}</span>
          </Row>
        </SubBlock>
      )}

      {data.orderBumpsAplicados.length > 0 && (
        <SubBlock>
          <h4>Order Bumps aplicados ({data.orderBumpsAplicados.length})</h4>
        </SubBlock>
      )}

      {data.downSells.length > 0 && (
        <SubBlock>
          <h4>Down Sells ({data.downSells.length})</h4>
        </SubBlock>
      )}

      {parcelas.length > 0 && (
        <SubBlock>
          <h4>Valores de parcelas</h4>
          <ParcelasGrid>
            {parcelas.map(([k, v]) => (
              <div key={k}>
                <span>{k}</span>
                <span>{formatPreco(v, data.moedaSigla)}</span>
              </div>
            ))}
          </ParcelasGrid>
        </SubBlock>
      )}

      {(data.backRedirect ||
        data.paginaObrigado ||
        data.pixEpirado ||
        data.pagamentoRecusado) && (
        <SubBlock>
          <h4>Páginas de redirecionamento</h4>
          {data.backRedirect && (
            <Row>
              <span>Voltar</span>
              <span>{data.backRedirect}</span>
            </Row>
          )}
          {data.paginaObrigado && (
            <Row>
              <span>Obrigado</span>
              <span>{data.paginaObrigado}</span>
            </Row>
          )}
          {data.pixEpirado && (
            <Row>
              <span>PIX expirado</span>
              <span>{data.pixEpirado}</span>
            </Row>
          )}
          {data.pagamentoRecusado && (
            <Row>
              <span>Pagamento recusado</span>
              <span>{data.pagamentoRecusado}</span>
            </Row>
          )}
        </SubBlock>
      )}
    </CollapsibleSection>
  );
};
